import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { Box, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { SpotlightCard, SpotlightPulse } from '@atlaskit/onboarding';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets';
import { fg } from '@confluence/feature-gating';
import { AIFC_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';

const styles = cssMap({
	root: {
		position: 'relative',
	},
	spotlight: {
		position: 'absolute',
		top: token('space.400'),
		right: token('space.negative.025'),
		marginBlockStart: token('space.075'),
	},
	pulse: {
		backgroundColor: token('color.background.input'),
		padding: 0,
		borderRadius: token('radius.small'),
	},
});

const i18n = defineMessages({
	rovoChatTitle: {
		id: 'onboarding-ai-first-create.nudge.rovo-chat.title',
		defaultMessage: 'Got work in progress?',
		description: 'Title for Rovo chat spotlight',
	},
	rovoChatDescription: {
		id: 'onboarding-ai-first-create.nudge.rovo-chat.description',
		defaultMessage:
			'Everything you create with Rovo is saved in chat. Select any preview card to continue editing.',
		description: 'Description text for Rovo chat spotlight',
	},
	rovoChatCTA: {
		id: 'onboarding-ai-first-create.nudge.rovo-chat.cta',
		defaultMessage: 'OK',
		description: 'CTA button text for Rovo chat spotlight',
	},
});

export type RovoChatSpotlightProps = {
	isConversationAssistantSidebarActive: boolean;
	children: React.ReactNode;
};

export const RovoChatSpotlight = ({
	isConversationAssistantSidebarActive,
	children,
}: RovoChatSpotlightProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);

	const { setOnboardingState } = useSetOnboardingState();

	const {
		data,
		loading: isGetOnboardingStateLoading,
		error: isGetOnboardingStateError,
	} = useGetOnboardingState(
		[...Object.values(AIFC_ONBOARDING_STATE_KEYS)],
		!fg('aifc_onboarding_killswitch') || !fg('aifc_create_enabled'),
	);

	const { isAIFCChatSpotlightActive, hasSeenAIFCChatSpotlight } = deserializeState(data);

	const handleSpotlightStart = useCallback(() => {
		setOnboardingState({
			key: AIFC_ONBOARDING_STATE_KEYS.hasSeenAIFCChatSpotlight,
			value: 'true',
		});

		setIsSpotlightActive(true);

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'RovoChatSpotlight',
				attributes: {
					source: 'onboarding-ai-first-create',
				},
			},
		}).fire();
	}, [createAnalyticsEvent, setOnboardingState]);

	const handleSpotlightStop = useCallback(() => {
		setIsSpotlightActive(false);
		setOnboardingState({
			key: AIFC_ONBOARDING_STATE_KEYS.isAIFCChatSpotlightActive,
			value: 'false',
		});
	}, [setOnboardingState]);

	const { useChoreographer } = useChoreographerAssets();

	const rovoChatSpotlightChoreographer = useChoreographer('RovoChatSpotlight', {
		start: handleSpotlightStart,
		stop: handleSpotlightStop,
	});

	const handleSpotlightInteraction = useCallback(
		(action: string, actionSubject: string, actionSubjectIdSuffix?: string) => {
			setIsSpotlightActive(false);

			rovoChatSpotlightChoreographer.stopMessage();

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action,
					actionSubject,
					actionSubjectId: `RovoChatSpotlight${actionSubjectIdSuffix ?? ''}`,
					source: 'onboarding-ai-first-create',
				},
			}).fire();
		},
		[createAnalyticsEvent, rovoChatSpotlightChoreographer],
	);

	useEffect(() => {
		if (
			!isGetOnboardingStateLoading &&
			!isGetOnboardingStateError &&
			isAIFCChatSpotlightActive &&
			!hasSeenAIFCChatSpotlight
		) {
			if (!isConversationAssistantSidebarActive) {
				rovoChatSpotlightChoreographer.startMessage();
			} else {
				setOnboardingState({
					key: AIFC_ONBOARDING_STATE_KEYS.isAIFCChatSpotlightActive,
					value: 'false',
				});
			}
		}
	}, [
		isGetOnboardingStateLoading,
		isGetOnboardingStateError,
		isAIFCChatSpotlightActive,
		hasSeenAIFCChatSpotlight,
		isConversationAssistantSidebarActive,
		rovoChatSpotlightChoreographer,
		setOnboardingState,
	]);

	if (!isSpotlightActive) {
		return <>{children}</>;
	}

	return (
		<Box xcss={styles.root} testId="rovo-chat-spotlight">
			<Box xcss={styles.spotlight}>
				<SpotlightCard
					width={275}
					heading={formatMessage(i18n.rovoChatTitle)}
					actions={[
						{
							text: formatMessage(i18n.rovoChatCTA),
							onClick: () => handleSpotlightInteraction('clicked', 'button', 'CTA'),
						},
					]}
				>
					{formatMessage(i18n.rovoChatDescription)}
				</SpotlightCard>
			</Box>
			<Box>
				<SpotlightPulse radius={3} pulse={isSpotlightActive}>
					<Pressable
						xcss={styles.pulse}
						onClick={() => handleSpotlightInteraction('clicked', 'button', 'Target')}
					>
						{children}
					</Pressable>
				</SpotlightPulse>
			</Box>
		</Box>
	);
};
